import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Question, PaperPlaneTilt, X, Brain, Clock, User, ChatCircleDots,
  CheckCircle, CircleNotch, Sparkle, ArrowsClockwise, TrashSimple,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { useLiveCounts } from "@/contexts/LiveCountsContext";
import { useSector } from "@/lib/sectorContext";
import Topbar from "@/components/Topbar";

/**
 * Perguntas Pendentes (Modelo B — Operator Relay).
 *
 * O operador vê aqui todas as perguntas que a IA escalou. Para cada
 * uma, escreve uma resposta CURTA e crua. A IA reformata no tom de
 * voz cadastrado e envia ao cliente final pelo WhatsApp.
 *
 * Opção "Salvar no Cérebro": vira documento no Knowledge para a IA
 * conseguir responder sozinha da próxima vez.
 */

const REASON_LABELS = {
  product_not_in_catalog: "Produto não catalogado",
  service_not_in_catalog: "Serviço não catalogado",
  delivery_or_pickup: "Entrega / retirada",
  payment_method: "Forma de pagamento",
  policy_unclear: "Política não estruturada",
  schedule_specific: "Horário específico",
  low_confidence: "IA sem confiança",
  other: "Outro",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.round(h / 24)}d`;
}

function ReasonBadge({ reason }) {
  return (
    <span
      data-testid={`pq-reason-${reason}`}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-amber-50 text-amber-900 border border-amber-200"
    >
      {REASON_LABELS[reason] || reason}
    </span>
  );
}

// Heurística local pra sugerir TTL ao operador (espelha
// engines/knowledge_lifecycle.py:suggest_ttl_days no backend — sem
// chamar API só pra evitar latência ao mudar o texto).
const PERISHABLE_RE = /\b(estoque|disponiv|em promoção|promoção|promocao|oferta|desconto|preço|preco|valor|quanto|r\$|reais?|à vista|a vista|parcela|frete|entrega hoje|entrega amanhã|entrega amanha|sábado|sabado|domingo|feriado|aberto hoje|abert[oa] amanha|abert[oa] amanhã)\b/i;
function smartTtlForBlob(blob) {
  return PERISHABLE_RE.test(blob || "") ? 30 : 0;
}

const TTL_OPTIONS = [
  { v: 0,   label: "Nunca expira" },
  { v: 7,   label: "7 dias" },
  { v: 30,  label: "30 dias" },
  { v: 90,  label: "90 dias" },
  { v: 365, label: "1 ano" },
];

function AnswerDrawer({ item, onClose, onAnswered, onOptimisticRemove }) {
  const { enablesKm, isChurch, isMandate, labels } = useSector();
  const [text, setText] = useState("");
  // v0.98 · Fev/2026 — Escolha ONDE salvar a resposta.
  //   • "customer" (padrão) — Cérebro DESTE cliente (customer_documents),
  //     isolado. Recomendado pra dados específicos.
  //   • "global" — Cérebro Geral (knowledge), aplicável a todos.
  //   • "none" — não salva, só responde ao cliente.
  const hasCustomer = !!item.customer_id;
  const [brainScope, setBrainScope] = useState(hasCustomer ? "customer" : "global");
  const saveBrain = brainScope === "global";
  const saveCustomerBrain = brainScope === "customer";
  const [ttlDays, setTtlDays] = useState(0);
  const [ttlTouched, setTtlTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  // Smart-detect: enquanto o operador não tocar no select, ajusta o
  // TTL automaticamente conforme a pergunta+resposta. Tocou? Para de
  // sobrescrever.
  useEffect(() => {
    if (ttlTouched) return;
    const detected = smartTtlForBlob(
      `${item?.question_text || ""}\n${text}`,
    );
    setTtlDays(detected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.question_text, text]);

  const submit = async () => {
    if (!text.trim()) {
      toast.error("Digite uma resposta antes de enviar.");
      return;
    }
    setBusy(true);
    // True optimistic — remove from list first, restore on failure.
    onOptimisticRemove?.(item.id);
    try {
      await api.post(`/pending-questions/${item.id}/answer`, {
        answer: text.trim(),
        save_to_brain: saveBrain,
        save_to_customer_brain: saveCustomerBrain,
        ttl_days: saveBrain ? (ttlDays > 0 ? ttlDays : null) : null,
      });
      toast.success("Resposta enviada ao cliente pela IA.");
      onAnswered?.();
      onClose();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Falha ao enviar a resposta. Tente novamente.",
      );
      // Roll back: trigger refetch so the card reappears if API failed.
      onAnswered?.();
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm("Descartar essa pergunta sem responder o cliente?")) return;
    setBusy(true);
    onOptimisticRemove?.(item.id);
    try {
      await api.post(`/pending-questions/${item.id}/dismiss`);
      toast.success("Pergunta descartada.");
      onAnswered?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao descartar.");
      onAnswered?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-end"
      data-testid="pq-drawer"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl bg-white border-l border-slate-200 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold mb-1">
              Operator Relay
            </div>
            <h3 className="text-xl font-semibold text-slate-900 tracking-tight">
              Alimentar a IA
            </h3>
            <p className="text-sm text-slate-500 mt-1 max-w-md">
              Escreva uma resposta curta. A IA reformata no tom da
              empresa e envia ao cliente no WhatsApp.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pq-drawer-close"
            className="text-slate-400 hover:text-slate-900 transition-colors"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Cliente */}
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-2">
              <User size={12} /> Cliente
            </div>
            <div className="text-[15px] font-medium text-slate-900">
              {item.customer_name || "Cliente sem nome"}
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              {timeAgo(item.created_at)} · <ReasonBadge reason={item.escalation_reason} />
            </div>
          </div>

          {/* Pergunta */}
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-2">
              <Question size={12} /> Pergunta do cliente
            </div>
            <div className="text-[14px] text-slate-800 leading-relaxed p-4 rounded-lg bg-slate-50 border border-slate-100 whitespace-pre-wrap">
              {item.question_text}
            </div>
          </div>

          {/* Resposta provisória */}
          {item.ia_provisional_reply && (
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-2">
                <Sparkle size={12} /> A IA já respondeu
              </div>
              <div className="text-[13px] text-slate-600 leading-relaxed p-3 rounded-lg bg-white border border-slate-200 italic whitespace-pre-wrap">
                &ldquo;{item.ia_provisional_reply}&rdquo;
              </div>
            </div>
          )}

          {/* Resposta do operador */}
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-2">
              <ChatCircleDots size={12} /> Sua resposta (cru — a IA reformata)
            </div>
            <textarea
              data-testid="pq-answer-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="Ex: Sim, temos R$ 980, em estoque, entrega só pra Brasília"
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-slate-400 transition-colors font-mono"
            />
            <p className="text-[11px] text-slate-400 mt-1.5">
              Seja direto. A IA cuida do &ldquo;Olá&rdquo;, do tom e do emoji.
            </p>
          </div>

          {/* Salvar no Cérebro — 3 modos (v0.98 · Fev/2026) */}
          <div data-testid="pq-brain-scope" className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
              <Brain size={11} weight="fill" /> Salvar essa resposta no Cérebro?
            </div>

            {/* Cérebro do cliente — recomendado quando há customer_id */}
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                brainScope === "customer"
                  ? "border-emerald-400 bg-emerald-50/60 ring-1 ring-emerald-200"
                  : "border-slate-200 hover:border-slate-300"
              } ${!hasCustomer ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="pq-scope-customer"
            >
              <input
                type="radio" name="brain_scope"
                checked={brainScope === "customer"}
                disabled={!hasCustomer}
                onChange={() => setBrainScope("customer")}
                className="mt-0.5 h-4 w-4 accent-emerald-700"
              />
              <div>
                <div className="text-[13px] font-medium text-slate-900 flex items-center gap-2">
                  Cérebro deste cliente
                  <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">
                    Recomendado
                  </span>
                </div>
                <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">
                  Isolado. Só usado em conversas com{" "}
                  <b>{item.customer_name || (isChurch ? "este irmão(ã)" : isMandate ? "este cidadão" : "este cliente")}</b>. Ex.:
                  {isChurch
                    ? " célula/ministério, pedido de oração, motivo pastoral, aniversário."
                    : isMandate
                      ? " demanda registrada, tema de interesse, bairro, histórico de atendimento."
                      : enablesKm
                        ? " histórico pessoal, endereço, veículo, alergias."
                        : " histórico pessoal, endereço, preferências, alergias."}
                  {!hasCustomer && (
                    <span className="text-amber-700"> Indisponível: pergunta sem {isChurch ? "irmão(ã)" : isMandate ? "cidadão" : "cliente"} atrelado.</span>
                  )}
                </p>
              </div>
            </label>

            {/* Cérebro geral — para políticas amplas */}
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                brainScope === "global"
                  ? "border-slate-900 bg-slate-50 ring-1 ring-slate-300"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="pq-scope-global"
            >
              <input
                type="radio" name="brain_scope"
                checked={brainScope === "global"}
                onChange={() => setBrainScope("global")}
                className="mt-0.5 h-4 w-4 accent-slate-900"
              />
              <div>
                <div className="text-[13px] font-medium text-slate-900">
                  Cérebro geral (todos os clientes)
                </div>
                <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">
                  Todos os clientes recebem esse conhecimento. Ex.:
                  políticas de preço, horários, promoções, procedimentos
                  padrão. Use com cuidado — evite dados específicos de UM cliente.
                </p>
              </div>
            </label>

            {/* Não salvar */}
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                brainScope === "none"
                  ? "border-slate-400 bg-slate-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="pq-scope-none"
            >
              <input
                type="radio" name="brain_scope"
                checked={brainScope === "none"}
                onChange={() => setBrainScope("none")}
                className="mt-0.5 h-4 w-4 accent-slate-500"
              />
              <div>
                <div className="text-[13px] font-medium text-slate-900">
                  Não salvar
                </div>
                <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">
                  Só responde o cliente agora. Nada vai pro Cérebro.
                </p>
              </div>
            </label>
          </div>

          {/* TTL — quanto tempo esse conhecimento vale */}
          {saveBrain && (
            <div
              data-testid="pq-ttl-block"
              className="p-3 rounded-lg border border-slate-200 bg-slate-50/40"
            >
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-2">
                <Clock size={11} /> Esse conhecimento expira em
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {TTL_OPTIONS.map((opt) => {
                  const active = ttlDays === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => { setTtlDays(opt.v); setTtlTouched(true); }}
                      data-testid={`pq-ttl-${opt.v}`}
                      className={`px-3 py-1 rounded-full text-[12px] tracking-tight transition-all border ${
                        active
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {!ttlTouched && ttlDays > 0 && (
                <p className="text-[10.5px] text-amber-700 mt-2 leading-snug flex items-start gap-1">
                  <Sparkle size={10} weight="fill" className="mt-0.5 shrink-0" />
                  Detectei que isso pode envelhecer (estoque/preço/promoção).
                  Sugeri {ttlDays} dias — você pode mudar.
                </p>
              )}
              {ttlDays === 0 && (
                <p className="text-[10.5px] text-slate-400 mt-2 leading-snug">
                  A IA usa esse conhecimento até você desativar manualmente.
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 bg-white">
          <button
            type="button"
            onClick={dismiss}
            disabled={busy}
            data-testid="pq-dismiss-btn"
            className="text-[13px] text-slate-500 hover:text-rose-600 transition-colors disabled:opacity-40"
          >
            Descartar sem responder
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !text.trim()}
            data-testid="pq-send-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-[13px] font-medium tracking-tight hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <PaperPlaneTilt size={14} weight="fill" />
            )}
            Enviar pela IA
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function PendingQuestions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshPending } = useLiveCounts();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("waiting");
  const [open, setOpen] = useState(null);

  // Iter-71.6 — deep-link: when the operator clicks a "Pending Question"
  // bell-notification we navigate here with `?focus={id}`. Auto-open the
  // matching item's drawer the moment the list arrives (or immediately
  // if it's already loaded). Saves the operator a second click + scroll.
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || !items) return;
    const match = items.find((it) => it.id === focusId);
    if (match && match.status === "waiting") {
      setOpen(match);
    }
    // Consume the param so a stray refresh doesn't re-open after the
    // operator already closed the drawer manually.
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
  }, [items, searchParams, setSearchParams]);

  // Solicita permissão de notificação UMA vez (best effort).
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const fetchItems = async () => {
    try {
      const r = await api.get("/pending-questions", {
        params: { status: filter, limit: 100 },
      });
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
      setItems([]);
    }
  };

  useEffect(() => { fetchItems(); }, [filter]);

  const counts = { waiting: 0, answered: 0, dismissed: 0 };
  for (const p of (items || [])) {
    if (p.status in counts) counts[p.status] += 1;
  }

  const refreshAll = () => {
    fetchItems();
    refreshPending?.();
  };

  const optimisticRemove = (id) => {
    setItems((cur) => (cur || []).filter((p) => p.id !== id));
  };

  // v0.98 · Fev/2026 — Descartar direto da lista + silenciar tópico.
  // A IA não escala mais mensagens do mesmo topic_tag por 90 dias.
  const quickDismiss = async (item) => {
    const ok = window.confirm(
      `Descartar "${(item.question_text || "").slice(0, 80)}"?\n\n` +
      `A IA vai ignorar mensagens semelhantes por 90 dias — ideal para ` +
      `saudações, comentários casuais e mensagens que não são perguntas ` +
      `de fato.`,
    );
    if (!ok) return;
    optimisticRemove(item.id);
    try {
      await api.post(
        `/pending-questions/${item.id}/dismiss?mute_topic=true&mute_days=90`,
      );
      toast.success("Descartada e silenciada. A IA não vai escalar isso de novo.");
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao descartar.");
      refreshAll();
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pending-questions-page">
      <Toaster theme="light" position="top-right" richColors />

      <Topbar
        title="Perguntas pendentes"
        subtitle="A IA escalou estas perguntas porque não tinha resposta estruturada. Responda em uma linha — a IA reformata e envia ao cliente."
        actions={
          <button
            type="button"
            onClick={refreshAll}
            data-testid="pq-refresh"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors text-[12px]"
          >
            <ArrowsClockwise size={12} /> Atualizar
          </button>
        }
      />

      <div className="px-8 pt-6 pb-12 max-w-[1200px] mx-auto">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-6 font-semibold">
          Operator Relay · Modelo B
        </div>
        {/* Filtros */}
        <div className="flex items-center gap-2 mb-6" data-testid="pq-filters">
          {[
            { id: "waiting", label: "Aguardando", n: counts.waiting },
            { id: "answered", label: "Respondidas", n: counts.answered },
            { id: "dismissed", label: "Descartadas", n: counts.dismissed },
            { id: "all", label: "Todas" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-testid={`pq-filter-${tab.id}`}
              onClick={() => setFilter(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] font-medium tracking-tight transition-all border ${
                filter === tab.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-900"
              }`}
            >
              {tab.label}
              {typeof tab.n === "number" && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  filter === tab.id
                    ? "bg-white/20 text-white"
                    : "bg-slate-100 text-slate-500"
                }`}>{tab.n}</span>
              )}
            </button>
          ))}
        </div>

        {/* Lista */}
        {items === null && (
          <div className="text-center py-16 text-slate-400">
            <CircleNotch size={20} className="animate-spin mx-auto mb-2" />
            Carregando…
          </div>
        )}

        {items?.length === 0 && (
          <div className="text-center py-16" data-testid="pq-empty">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-50 text-slate-400 mb-3">
              <CheckCircle size={20} weight="regular" />
            </div>
            <div className="text-[15px] font-medium text-slate-900">
              Nada pendente por aqui.
            </div>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              Quando a IA não souber alguma resposta, ela vai aparecer
              aqui para você alimentar com a informação correta.
            </p>
          </div>
        )}

        {items && items.length > 0 && (
          <div className="space-y-3" data-testid="pq-list">
            {items.map((it) => (
              <div key={it.id} className="relative">
                <button
                  type="button"
                  onClick={() => it.status === "waiting" && setOpen(it)}
                  disabled={it.status !== "waiting"}
                  data-testid={`pq-item-${it.id}`}
                  className={`w-full text-left p-5 rounded-xl border transition-all group ${
                    it.status === "waiting"
                      ? "bg-white border-slate-200 hover:border-slate-900 hover:shadow-sm cursor-pointer"
                      : "bg-slate-50 border-slate-100 cursor-default"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-semibold shrink-0 ${
                      it.status === "waiting"
                        ? "bg-amber-100 text-amber-900"
                        : it.status === "answered"
                        ? "bg-emerald-100 text-emerald-900"
                        : "bg-slate-200 text-slate-500"
                    }`}>
                      {(it.customer_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[14px] font-semibold text-slate-900 tracking-tight">
                          {it.customer_name || "Cliente sem nome"}
                        </span>
                        <ReasonBadge reason={it.escalation_reason} />
                        {it.saved_to_brain && (
                          <span
                            title="Salva no Cérebro"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-violet-50 text-violet-900 border border-violet-200"
                          >
                            <Brain size={9} weight="fill" /> No Cérebro
                          </span>
                        )}
                      </div>
                      <p className="text-[14px] text-slate-800 leading-relaxed line-clamp-2">
                        {it.question_text}
                      </p>
                      {it.operator_answer && (
                        <p className="text-[12.5px] text-slate-500 italic mt-2 line-clamp-2">
                          Você respondeu: &ldquo;{it.operator_answer}&rdquo;
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-400">
                        <span className="inline-flex items-center gap-1">
                          <Clock size={10} /> {timeAgo(it.created_at)}
                        </span>
                        {it.confidence_score > 0 && (
                          <span>confiança detector: {Math.round(it.confidence_score * 100)}%</span>
                        )}
                      </div>
                    </div>
                    {it.status === "waiting" && (
                      <div className="shrink-0 text-[11px] text-slate-400 uppercase tracking-wider group-hover:text-slate-900 transition-colors">
                        Responder →
                      </div>
                    )}
                  </div>
                </button>

                {/* v0.98 · Fev/2026 — Botão de descartar/silenciar na card */}
                {it.status === "waiting" && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      quickDismiss(it);
                    }}
                    data-testid={`pq-inline-dismiss-${it.id}`}
                    title="Descartar e silenciar esse tópico (IA não escala mais igual)"
                    className="absolute top-3 right-3 p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                    style={{ opacity: 1 }}
                  >
                    <TrashSimple size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <AnswerDrawer
          item={open}
          onClose={() => setOpen(null)}
          onAnswered={refreshAll}
          onOptimisticRemove={optimisticRemove}
        />
      )}
    </div>
  );
}
